/**
 * CampoSelecionar — input somente-leitura com ícone de lupa/limpar
 *
 * Props
 *   label       string                 ex: "Imóvel *"
 *   valor       string                 texto exibido no campo
 *   onAbrir     () => void             abre o modal de seleção
 *   onLimpar    () => void             limpa o item selecionado
 *   readOnly    boolean  (opcional)    desativa interação (modo visualização)
 *   erro        string   (opcional)    mensagem de erro (helperText + borda vermelha)
 *   sx          object   (opcional)    sx extra para o TextField
 *   fullWidth   boolean  (opcional)
 *   minWidth    number   (opcional)    padrão 280
 *   inputRef    ref      (opcional)    pra focar o campo programaticamente (ex.: ao abrir p/ editar/visualizar)
 *
 * Comportamento visual
 *   readOnly=true          → sem ícone, fundo #252525, cursor default
 *   readOnly=false + vazio → lupa vermelha, cursor pointer, click abre modal
 *   readOnly=false + valor → × amarelo, cursor default, click limpa seleção
 *
 * Exemplo de uso
 *   const [aberto, setAberto]    = useState(false);
 *   const [selecionado, setSel]  = useState(null);
 *
 *   <CampoSelecionar
 *     label="Imóvel *"
 *     valor={selecionado?.apelido ?? ""}
 *     onAbrir={() => setAberto(true)}
 *     onLimpar={() => setSelecionado(null)}
 *     erro={erros.imovel}
 *   />
 *   <ModalSelecionarImovel
 *     open={aberto}
 *     onClose={() => setAberto(false)}
 *     onSelect={setSelecionado}
 *   />
 */
import React from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

export default function CampoSelecionar({ label, valor = "", onAbrir, onLimpar, readOnly = false, erro, sx = {}, fullWidth, minWidth = 280, inputRef }) {
  const temValor = Boolean(valor);
  const clicavel = !readOnly && !temValor;

  return (
    <TextField
      label={label}
      value={valor}
      fullWidth={fullWidth}
      inputRef={inputRef}
      onClick={clicavel ? onAbrir : undefined}
      error={Boolean(erro)}
      helperText={erro}
      slotProps={{
        input: {
          readOnly: true,
          style: { cursor: clicavel ? "pointer" : "default" },
          endAdornment: !readOnly ? (
            <InputAdornment position="end">
              {temValor ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onLimpar();
                  }}
                  sx={{ color: "var(--ig-t-f59e0b)" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAbrir();
                  }}
                  sx={{ color: "#e53935" }}
                >
                  <SearchIcon />
                </IconButton>
              )}
            </InputAdornment>
          ) : null,
        },
      }}
      sx={{
        minWidth,
        "& .MuiInputLabel-root": {
          color: erro ? "#f44336" : "var(--ig-t-ffffff)",
          fontWeight: "bold",
        },
        "& .MuiOutlinedInput-root": {
          backgroundColor: readOnly ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
          color: "var(--ig-t-ffffff)",
          cursor: clicavel ? "pointer" : "default",
          "& fieldset": { borderColor: readOnly ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-444444)" },
          "&:hover fieldset": { borderColor: readOnly ? "var(--ig-b-333333)" : erro ? "#f44336" : "var(--ig-b-666666)" },
        },
        "& input": { color: "var(--ig-t-ffffff)" },
        "& .MuiFormHelperText-root": { color: "#f44336" },
        ...sx,
      }}
    />
  );
}
